import React, { useCallback } from 'react';
import { Breadcrumbs, Breadcrumb } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { omit } from 'ramda';
import { titleize } from '@moda/portal-stanchions';
import { FILTER_KEYS } from '../../hooks/usePLPParams';
import { useUrlParams } from '../../hooks/useUrlParams';
import { findRouteFor, hrefFor } from '../../routers';
import { tc } from '../../lib/trackingContext';
import { usePLPFirstBreadcrumbTitle } from './usePLPFirstBreadcrumbTitle';

import './PLPBreadcrumbs.scss';

export const PLPBreadcrumbs = () => {
  const { getHrefFor, params } = useUrlParams();
  const location = useLocation();
  const route = findRouteFor(location.pathname);

  const firstBreadcrumbTitle = usePLPFirstBreadcrumbTitle();

  const vertical = Array.isArray(params.vertical) ? params.vertical[0] : params.vertical;
  const category = Array.isArray(params.category) ? params.category[0] : params.category;
  const subcategory = Array.isArray(params.subcategory)
    ? params.subcategory[0]
    : params.subcategory;

  const getVerticalHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['category', 'subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return hrefFor.HomePage();
    }
  }, [params, getHrefFor]);

  const getCategoryHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return '';
    }
  }, [params, getHrefFor]);

  const getBaseHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['vertical', 'category', 'subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return '';
    }
  }, [params, getHrefFor]);

  const handleTrackBreadcrumbClicked = useCallback((url: string | null, text: string) => {
    tc.track('Breadcrumb Clicked', {
      addToTrackingEvent: {
        url,
        text: titleize(text),
        label: titleize(text)
      }
    });
  }, []);

  return (
    <Breadcrumbs className="PLPBreadcrumbs">
      {firstBreadcrumbTitle && (
        <Breadcrumb
          onClick={() =>
            handleTrackBreadcrumbClicked(vertical ? getBaseHref() : null, firstBreadcrumbTitle)
          }
          to={vertical ? getBaseHref() : undefined}
        >
          {titleize(firstBreadcrumbTitle)}
        </Breadcrumb>
      )}

      {vertical && (
        <Breadcrumb
          onClick={() =>
            handleTrackBreadcrumbClicked(category ? getVerticalHref() : null, vertical)
          }
          to={category ? getVerticalHref() : undefined}
        >
          {titleize(vertical)}
        </Breadcrumb>
      )}

      {route?.key === 'ProductCategoryListingPage' &&
        !params.section &&
        category &&
        ['fine-jewelry', 'fashion-jewelry'].includes(category) && (
          <Breadcrumb
            onClick={() =>
              handleTrackBreadcrumbClicked(
                getHrefFor({
                  ...omit(['category', 'subcategory', ...FILTER_KEYS], params),
                  category: 'jewelry'
                }),
                'Jewelry'
              )
            }
            to={getHrefFor({
              ...omit(['category', 'subcategory', ...FILTER_KEYS], params),
              category: 'jewelry'
            })}
          >
            Jewelry
          </Breadcrumb>
        )}

      {category && (
        <Breadcrumb
          onClick={() =>
            handleTrackBreadcrumbClicked(subcategory ? getCategoryHref() : null, category)
          }
          to={subcategory ? getCategoryHref() : undefined}
        >
          {titleize(category)}
        </Breadcrumb>
      )}

      {subcategory && <Breadcrumb>{titleize(subcategory)}</Breadcrumb>}
    </Breadcrumbs>
  );
};
